import {
  Button,
  Checkbox,
  cn,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
} from "@gearbox-protocol/deploy-tools-ui";
import EnvInputs from "components/forms/EnvInputs";
import { useCallback } from "react";
import type { Control, FieldValues, Path, PathValue } from "react-hook-form";
import { useFormContext, useWatch } from "react-hook-form";
import { useToggle } from "react-use";

import type { BackendSchema } from "./schema";

export const DEFAULT_BACKEND_VERSION = "staging-testnet";

export interface BackendFormValues extends FieldValues {
  backend?: BackendSchema;
}

interface ForkBackendProps<TFieldValues extends BackendFormValues> {
  control: Control<TFieldValues>;
  compact?: boolean;
}

const ForkBackend = <TFieldValues extends BackendFormValues>({
  control,
  compact,
}: ForkBackendProps<TFieldValues>) => {
  const { setValue } = useFormContext<TFieldValues>();
  const backendField = "backend" as Path<TFieldValues>;
  const versionField = "backend.version" as Path<TFieldValues>;
  const envField = "backend.env" as Path<TFieldValues>;
  const backend = useWatch({ control, name: backendField }) as
    | BackendSchema
    | undefined;
  const enabled = backend != null;

  const [isExpanded, toggleExpanded] = useToggle(
    !!backend?.version || !!backend?.env?.length,
  );

  const toggleEnabled = useCallback(
    (checked: boolean) => {
      if (checked) {
        setValue(backendField, {
          version: DEFAULT_BACKEND_VERSION,
        } as PathValue<TFieldValues, Path<TFieldValues>>);
      } else {
        setValue(
          backendField,
          undefined as PathValue<TFieldValues, Path<TFieldValues>>,
        );
        toggleExpanded(false);
      }
    },
    [backendField, setValue, toggleExpanded],
  );

  return (
    <div className="flex flex-col gap-y-2 px-4 py-2 border rounded-md">
      <div className="flex flex-row items-center justify-between">
        <FormItem className="flex flex-row items-center gap-x-2">
          <FormControl>
            <Checkbox checked={enabled} onCheckedChange={toggleEnabled} />
          </FormControl>
          <FormLabel>Enable backend</FormLabel>
        </FormItem>

        {enabled && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={toggleExpanded}
          >
            {isExpanded ? "...show less" : "...show more"}
          </Button>
        )}
      </div>

      {enabled && isExpanded && (
        <>
          <FormField
            control={control}
            name={versionField}
            render={({ field }) => (
              <FormItem
                className={cn(
                  "flex",
                  compact ? "flex-col gap-y-1" : "flex-row items-center",
                )}
              >
                <FormLabel className={compact ? undefined : "w-24"}>
                  Version
                </FormLabel>
                <FormControl>
                  <Input
                    placeholder={DEFAULT_BACKEND_VERSION}
                    {...field}
                    value={field.value ?? ""}
                    className={cn("h-8", compact ? "w-full" : "w-[300px]")}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={control}
            name={envField}
            render={({ field }) => (
              <FormItem
                className={cn(
                  "flex gap-x-1",
                  compact ? "flex-col" : "flex-row",
                )}
              >
                <FormLabel className={compact ? undefined : "w-24 pt-2"}>
                  Environment variables
                </FormLabel>
                <EnvInputs field={field} compact={compact} />
              </FormItem>
            )}
          />
        </>
      )}
    </div>
  );
};

export default ForkBackend;
